/**
 * Fullscreen media viewer: pinch and double-tap zoom, pan, swipe between examples, and
 * swipe-down to dismiss.
 *
 * On an image-prompt library this is a core interaction, not a nicety — the example *is* the
 * argument for the prompt, and a 400px card cannot make it. It had no fullscreen view at all.
 *
 * Four decisions shape the implementation:
 *
 * **All gesture state lives in refs, none in React state.** A pinch fires pointermove at screen
 * refresh rate; routing each one through a render would drop frames on exactly the mid-range
 * Android phones this has to feel good on. React re-renders only when the *page* changes.
 *
 * **One pointer model for everything.** Pointer Events unify touch, mouse and pen, so pinch,
 * pan, swipe and drag-to-dismiss are all the same two-pointer bookkeeping rather than four
 * separate handlers. It also means the whole thing is testable with a mouse.
 *
 * **Which gesture wins is decided once, at the start of a drag, and then held.** Deciding per
 * frame is what makes a viewer feel like it is fighting you: a swipe that drifts 3px vertically
 * must not turn into a dismiss halfway through.
 *
 * **Zoomed pans never steal the page swipe until the edge is reached.** Panning right on a
 * zoomed image should move the image, and only start turning the page once there is no image
 * left to reveal — the behaviour every native photo viewer has and every naive one gets wrong.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ExampleMedia } from '@promptvault/shared';
import { useI18n } from '../lib/i18n';
import { haptic } from '../lib/telegram';
import { Icon } from './Icon';

const MAX_SCALE = 4;
const DOUBLE_TAP_SCALE = 2.5;
/** Past this fraction of the viewport height, releasing dismisses instead of springing back. */
const DISMISS_FRACTION = 0.22;
const DOUBLE_TAP_MS = 300;
const SWIPE_VELOCITY = 500;

type Mode = 'idle' | 'deciding' | 'page' | 'pan' | 'pinch' | 'dismiss';

interface Transform {
  scale: number;
  x: number;
  y: number;
}

const IDENTITY: Transform = { scale: 1, x: 0, y: 0 };

function distance(a: { x: number; y: number }, b: { x: number; y: number }): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

/**
 * A video in the fullscreen viewer: our own player, always, plus a link to the channel post.
 *
 * ## Why the Telegram embed was removed
 *
 * V20 made the post-widget iframe the primary player, on evidence gathered by loading
 * `t.me/<channel>/<id>?embed=1` **as a top-level page** in Chromium. That is not the thing that
 * ships. The thing that ships is a cross-origin iframe inside Telegram's own webview, and it was
 * never tested there.
 *
 * The owner then reported the Mini App did not show, load, *or link* the video, and the code says
 * exactly why:
 *
 * 1. `channelUrl` is non-null for all 408 videos, so the embed branch was always taken.
 * 2. `onLoad` fires for a frame that rendered nothing as reliably as for one that rendered a post.
 *    So `embedReady` went true, the three-second fallback timer was cleared, and the native player
 *    could never take over. The guard waited for a signal that arrives on failure too.
 * 3. The "Play in Telegram" link lived only in the *fallback* branch — the branch that never ran.
 *
 * An empty box and no way out. Both halves of the report, from one inversion.
 *
 * ## What replaces it
 *
 * `<video>` against `/media/<key>`, which is measured: 200 with a real mp4, and 206 with the
 * correct `content-range` for mid-range, suffix and open-ended requests — for videos served from
 * Telegram *and* for the nine oversized ones still on R2. The poster is always set, so a grid shows
 * a frame immediately rather than waiting on bytes. The channel link is rendered unconditionally
 * beside it, because it is the guaranteed path and must never be the thing that is missing.
 *
 * The embed saved Worker range requests against a 100k/day tier. At current traffic that saving is
 * theoretical; the breakage was not. See DECISIONS.md D44.
 */
function VideoPane({
  example,
  label,
  active,
}: {
  example: ExampleMedia;
  label: string;
  active: boolean;
}) {
  return (
    <div className="relative flex max-h-full max-w-full items-center justify-center">
      <video
        src={example.url ?? undefined}
        poster={example.thumbUrl ?? undefined}
        controls
        playsInline
        preload="metadata"
        autoPlay={active}
        // Controls need pointer events; the surrounding surface still handles swipes
        // outside the video box.
        style={{ maxHeight: '100%', maxWidth: '100%', touchAction: 'auto' }}
      />
      {example.channelUrl ? (
        <a
          href={example.channelUrl}
          target="_blank"
          rel="noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="absolute bottom-3 right-3 rounded-full bg-black/70 px-3 py-1.5 text-xs font-medium text-white backdrop-blur"
        >
          {label}
        </a>
      ) : null}
    </div>
  );
}

export function Lightbox({
  examples,
  startIndex = 0,
  alt,
  onClose,
}: {
  examples: ExampleMedia[];
  startIndex?: number;
  alt: string;
  onClose: () => void;
}) {
  const { t, rtl } = useI18n();
  const [index, setIndex] = useState(startIndex);
  const [closing, setClosing] = useState(false);

  const surface = useRef<HTMLDivElement>(null);
  const track = useRef<HTMLDivElement>(null);
  const imageWrap = useRef<HTMLDivElement>(null);

  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const gesture = useRef({
    mode: 'idle' as Mode,
    startX: 0,
    startY: 0,
    lastX: 0,
    lastY: 0,
    lastT: 0,
    velocity: 0,
    pinchStart: 0,
    scaleStart: 1,
    originX: 0,
    originY: 0,
    pageBase: 0,
    lastTapAt: 0,
    lastTapX: 0,
  });
  const transform = useRef<Transform>({ ...IDENTITY });
  const [zoomed, setZoomed] = useState(false);

  const count = examples.length;
  const reduced = useMemo(
    () =>
      typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches,
    [],
  );

  /** Paint the current transform. Never goes through React — see the header note. */
  const paint = useCallback((animate = false) => {
    const el = imageWrap.current;
    if (!el) return;
    const { scale, x, y } = transform.current;
    el.style.transition = animate ? 'transform 260ms cubic-bezier(0.23, 1, 0.32, 1)' : 'none';
    el.style.transform = `translate3d(${x}px, ${y}px, 0) scale(${scale})`;
  }, []);

  const paintTrack = useCallback((offset: number, animate = false) => {
    const el = track.current;
    if (!el) return;
    el.style.transition = animate ? 'transform 280ms cubic-bezier(0.23, 1, 0.32, 1)' : 'none';
    el.style.transform = `translate3d(${offset}px, 0, 0)`;
  }, []);

  const paintSurface = useCallback((dy: number, animate = false) => {
    const el = surface.current;
    if (!el) return;
    const progress = Math.min(1, Math.abs(dy) / (window.innerHeight * 0.6));
    el.style.transition = animate ? 'background-color 200ms ease-out' : 'none';
    // The backdrop thins as the image is dragged away, so the gesture reads as "putting it
    // back" rather than "moving a thing on top of a black wall".
    // `color-mix` over the token rather than a hand-built `rgba()`: the alpha is computed, the
    // colour is not, and D13 keeps every colour literal in tokens.css.
    const alpha = ((1 - progress * 0.75) * 100).toFixed(1);
    el.style.backgroundColor = `color-mix(in srgb, var(--pv-lightbox-bg) ${alpha}%, transparent)`;
  }, []);

  const resetZoom = useCallback(
    (animate = true) => {
      transform.current = { ...IDENTITY };
      paint(animate);
      setZoomed(false);
    },
    [paint],
  );

  /** Keep a zoomed image's edges inside the viewport rather than letting it drift off-screen. */
  const clampPan = useCallback(() => {
    const el = imageWrap.current;
    if (!el) return;
    const { scale } = transform.current;
    if (scale <= 1) {
      transform.current.x = 0;
      transform.current.y = 0;
      return;
    }
    const rect = el.getBoundingClientRect();
    const overflowX = Math.max(0, (rect.width - window.innerWidth) / 2);
    const overflowY = Math.max(0, (rect.height - window.innerHeight) / 2);
    transform.current.x = Math.max(-overflowX, Math.min(overflowX, transform.current.x));
    transform.current.y = Math.max(-overflowY, Math.min(overflowY, transform.current.y));
  }, []);

  const close = useCallback(() => {
    if (closing) return;
    setClosing(true);
    haptic('light');
    // Let the exit transition land before unmounting, unless motion is reduced.
    if (reduced) onClose();
    else setTimeout(onClose, 200);
  }, [closing, onClose, reduced]);

  const goTo = useCallback(
    (next: number, animate = true) => {
      const clamped = Math.max(0, Math.min(count - 1, next));
      if (clamped !== index) {
        haptic('light');
        setIndex(clamped);
        resetZoom(false);
      }
      paintTrack(-clamped * window.innerWidth, animate);
    },
    [count, index, paintTrack, resetZoom],
  );

  /** ── Lifecycle ──────────────────────────────────────────────────────────────────────── */

  useEffect(() => {
    haptic('light');
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    paintTrack(-startIndex * window.innerWidth, false);
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [paintTrack, startIndex]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
      // Arrow keys follow reading order, so in RTL they mirror like everything else.
      else if (e.key === 'ArrowRight') goTo(index + (rtl ? -1 : 1));
      else if (e.key === 'ArrowLeft') goTo(index + (rtl ? 1 : -1));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [close, goTo, index, rtl]);

  // The viewport changes when Telegram expands or the device rotates; a track parked at the
  // old width would sit between pages.
  useEffect(() => {
    const onResize = () => paintTrack(-index * window.innerWidth, false);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [index, paintTrack]);

  /** ── Gestures ───────────────────────────────────────────────────────────────────────── */

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      const g = gesture.current;
      pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

      if (pointers.current.size === 2) {
        const [a, b] = [...pointers.current.values()];
        g.mode = 'pinch';
        g.pinchStart = distance(a!, b!);
        g.scaleStart = transform.current.scale;
        g.originX = (a!.x + b!.x) / 2;
        g.originY = (a!.y + b!.y) / 2;
        return;
      }

      g.mode = 'deciding';
      g.startX = g.lastX = e.clientX;
      g.startY = g.lastY = e.clientY;
      g.lastT = performance.now();
      g.velocity = 0;
      g.pageBase = -index * window.innerWidth;
    },
    [index],
  );

  const onPointerMove = useCallback(
    (e: PointerEvent | React.PointerEvent) => {
      const g = gesture.current;
      if (!pointers.current.has(e.pointerId)) return;
      pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

      if (g.mode === 'pinch' && pointers.current.size >= 2) {
        const [a, b] = [...pointers.current.values()];
        const next = Math.max(1, Math.min(MAX_SCALE, (g.scaleStart * distance(a!, b!)) / g.pinchStart)); // prettier-ignore
        transform.current.scale = next;
        clampPan();
        paint();
        return;
      }

      const dx = e.clientX - g.startX;
      const dy = e.clientY - g.startY;

      /**
       * Commit to one gesture, once, after 8px of travel. Re-deciding every frame is what
       * makes a viewer feel like it is arguing with the user.
       */
      if (g.mode === 'deciding') {
        if (Math.hypot(dx, dy) < 8) return;
        if (transform.current.scale > 1) g.mode = 'pan';
        else if (Math.abs(dy) > Math.abs(dx) * 1.2 && dy > 0) g.mode = 'dismiss';
        else g.mode = 'page';
      }

      const now = performance.now();
      const dt = now - g.lastT;
      if (dt > 4) {
        g.velocity = ((e.clientX - g.lastX) / dt) * 1000;
        g.lastX = e.clientX;
        g.lastY = e.clientY;
        g.lastT = now;
      }

      if (g.mode === 'pan') {
        const before = transform.current.x;
        transform.current.x += e.movementX || 0;
        transform.current.y += e.movementY || 0;
        clampPan();
        // Pan hit the edge and the finger is still going: hand over to the page swipe, which
        // is what a native viewer does rather than simply stopping dead.
        if (transform.current.x === before && Math.abs(dx) > 24 && before !== 0) {
          g.mode = 'page';
          g.startX = e.clientX;
        } else {
          paint();
          return;
        }
      }

      if (g.mode === 'page') {
        const width = window.innerWidth;
        let offset = g.pageBase + (e.clientX - g.startX);
        const min = -(count - 1) * width;
        if (offset > 0) offset *= 0.34;
        if (offset < min) offset = min + (offset - min) * 0.34;
        paintTrack(offset);
        return;
      }

      if (g.mode === 'dismiss') {
        transform.current.y = dy;
        // Shrinking slightly while dragging away is the affordance that says "let go".
        transform.current.scale = Math.max(0.86, 1 - Math.abs(dy) / (window.innerHeight * 2.4));
        paint();
        paintSurface(dy);
      }
    },
    [clampPan, count, paint, paintSurface, paintTrack],
  );

  const onPointerUp = useCallback(
    (e: PointerEvent | React.PointerEvent) => {
      const g = gesture.current;
      pointers.current.delete(e.pointerId);

      if (g.mode === 'pinch') {
        // A pinch that ended below 1× snaps back rather than leaving a shrunken image.
        if (transform.current.scale <= 1.02) resetZoom();
        else {
          setZoomed(true);
          clampPan();
          paint(true);
        }
        g.mode = pointers.current.size > 0 ? 'deciding' : 'idle';
        return;
      }

      if (g.mode === 'page') {
        const width = window.innerWidth;
        const travelled = e.clientX - g.startX;
        const flung = Math.abs(g.velocity) > SWIPE_VELOCITY;
        const far = Math.abs(travelled) > width * 0.28;
        const direction = travelled < 0 ? 1 : -1;
        goTo(flung || far ? index + direction : index);
      } else if (g.mode === 'dismiss') {
        const dy = e.clientY - g.startY;
        if (dy > window.innerHeight * DISMISS_FRACTION) {
          close();
        } else {
          resetZoom();
          paintSurface(0, true);
        }
      } else if (g.mode === 'pan') {
        setZoomed(transform.current.scale > 1);
        clampPan();
        paint(true);
      } else if (g.mode === 'deciding') {
        /** A tap that never became a drag: double-tap zooms, single tap does nothing. */
        const now = performance.now();
        const isDouble = now - g.lastTapAt < DOUBLE_TAP_MS && Math.abs(e.clientX - g.lastTapX) < 40;
        g.lastTapAt = now;
        g.lastTapX = e.clientX;
        if (isDouble) {
          haptic('light');
          if (transform.current.scale > 1) resetZoom();
          else {
            // Zoom toward the tap, so double-tapping a face centres that face.
            const cx = e.clientX - window.innerWidth / 2;
            const cy = e.clientY - window.innerHeight / 2;
            transform.current = {
              scale: DOUBLE_TAP_SCALE,
              x: -cx * (DOUBLE_TAP_SCALE - 1),
              y: -cy * (DOUBLE_TAP_SCALE - 1),
            };
            clampPan();
            paint(true);
            setZoomed(true);
          }
        }
      }

      g.mode = pointers.current.size > 0 ? 'deciding' : 'idle';
    },
    [clampPan, close, goTo, index, paint, paintSurface, resetZoom],
  );

  /**
   * Move and up are tracked on `window`, not on the element.
   *
   * The obvious implementation — `onPointerMove` on the surface plus `setPointerCapture` —
   * silently loses every event after pointerdown: instrumenting the page showed 16 pointer
   * events reaching `window` and exactly 2 reaching the dialog, so a swipe registered its start
   * and nothing else. Whatever the retargeting rule, listening at the window is both what works
   * and what is correct: a drag that travels past the edge of the element, or past a `<video>`
   * that swallows events, is still the same gesture and must still be tracked.
   */
  useEffect(() => {
    const move = (e: PointerEvent) => onPointerMove(e);
    const up = (e: PointerEvent) => onPointerUp(e);
    window.addEventListener('pointermove', move, { passive: true });
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
    };
  }, [onPointerMove, onPointerUp]);

  const current = examples[index];
  const isVideo = current?.mediaType === 'video';

  return (
    <div
      ref={surface}
      role="dialog"
      aria-modal="true"
      aria-label={alt}
      // Always LTR: page 1 is on the left in every language, because the gesture is spatial
      // rather than textual. The dots and counter below mirror; the film strip does not.
      dir="ltr"
      className="fixed inset-0 z-50 touch-none select-none"
      style={{
        backgroundColor: 'var(--pv-lightbox-bg)',
        opacity: closing ? 0 : 1,
        transition: reduced ? 'none' : 'opacity 200ms ease-out',
      }}
      onPointerDown={onPointerDown}
    >
      <div ref={track} className="flex h-full" style={{ willChange: 'transform' }}>
        {examples.map((example, i) => (
          <div
            key={example.id ?? i}
            className="flex h-full flex-none items-center justify-center"
            style={{ width: '100vw' }}
          >
            <div
              ref={i === index ? imageWrap : undefined}
              className="flex h-full w-full items-center justify-center"
              style={{ willChange: 'transform' }}
            >
              {example.mediaType === 'video' ? (
                <VideoPane example={example} label={t.playInTelegram} active={i === index} />
              ) : (
                <img
                  src={example.url ?? example.thumbUrl ?? undefined}
                  alt={i === index ? alt : ''}
                  draggable={false}
                  className="max-h-full max-w-full object-contain"
                  // Only the visible page loads eagerly; the neighbours arrive as they are
                  // swiped to, which matters on a slow connection with four 1600px examples.
                  loading={i === startIndex ? 'eager' : 'lazy'}
                />
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Chrome fades out while zoomed so it never sits on top of what is being inspected. */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          opacity: zoomed ? 0 : 1,
          transition: reduced ? 'none' : 'opacity 180ms ease-out',
        }}
      >
        <button
          type="button"
          aria-label={t.close}
          onClick={close}
          onPointerDown={(e) => e.stopPropagation()}
          className="pointer-events-auto absolute end-3 flex h-10 w-10 items-center justify-center rounded-full"
          style={{
            top: 'calc(env(safe-area-inset-top, 0px) + 12px)',
            background: 'var(--pv-media-scrim)',
            backdropFilter: 'blur(12px)',
            color: 'var(--pv-on-media)',
          }}
        >
          <Icon name="close" size={20} />
        </button>

        {count > 1 && (
          <>
            <div
              dir={rtl ? 'rtl' : 'ltr'}
              className="tnum absolute start-4 rounded-full px-2.5 py-1 text-caption font-medium"
              style={{
                top: 'calc(env(safe-area-inset-top, 0px) + 14px)',
                background: 'var(--pv-media-scrim)',
                color: 'var(--pv-on-media)',
              }}
            >
              {index + 1} / {count}
            </div>
            <div
              className="absolute inset-x-0 flex items-center justify-center gap-1.5"
              style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 18px)' }}
            >
              {examples.map((_, i) => (
                <span
                  key={i}
                  className="h-1.5 rounded-full"
                  style={{
                    width: i === index ? 18 : 6,
                    background: i === index ? 'var(--pv-on-media)' : 'rgb(255 255 255 / 0.45)',
                    transition: reduced ? 'none' : 'width 200ms cubic-bezier(0.23, 1, 0.32, 1)',
                  }}
                />
              ))}
            </div>
          </>
        )}

        {/* A hint only while it is still useful — once zoomed, the gesture is discovered. */}
        {!zoomed && !isVideo && (
          <div
            className="absolute inset-x-0 text-center text-caption"
            style={{
              bottom: `calc(env(safe-area-inset-bottom, 0px) + ${count > 1 ? 40 : 20}px)`,
              color: 'rgba(255, 255, 255, 0.5)',
            }}
          >
            {t.lightboxHint}
          </div>
        )}
      </div>
    </div>
  );
}
